import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const ActiveWorkoutContext = createContext(null);
const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const useActiveWorkout = () => {
  const ctx = useContext(ActiveWorkoutContext);
  if (!ctx) throw new Error('useActiveWorkout must be used within ActiveWorkoutProvider');
  return ctx;
};

export const ActiveWorkoutProvider = ({ children }) => {
  const { user } = useAuth();
  const [activeWorkout, setActiveWorkout] = useState(null);
  const [now, setNow] = useState(() => Date.now());

  const refresh = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await axios.get(`${API_URL}/workouts/active`, { withCredentials: true });
      setActiveWorkout(data);
    } catch {
      setActiveWorkout(null);
    }
  }, [user]);

  useEffect(() => {
    if (user) refresh();
  }, [user, refresh]);

  // Global session tick — 1s cadence while a workout is active
  useEffect(() => {
    if (!activeWorkout?.started_at) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [activeWorkout?.started_at]);

  const elapsedSeconds = useMemo(() => {
    if (!activeWorkout?.started_at) return 0;
    // Defensive: if the server sends an ISO datetime without a timezone
    // suffix (naive UTC), JS will parse it as LOCAL time — creating a
    // large positive offset. Force-append `Z` so the timer always begins
    // at 0s regardless of the browser's timezone.
    let iso = activeWorkout.started_at;
    if (typeof iso === 'string' && !/[Zz]|[+-]\d\d:?\d\d$/.test(iso)) {
      iso = iso + 'Z';
    }
    const started = new Date(iso).getTime();
    if (Number.isNaN(started)) return 0;
    return Math.max(0, Math.floor((now - started) / 1000));
  }, [activeWorkout?.started_at, now]);

  return (
    <ActiveWorkoutContext.Provider value={{ activeWorkout, setActiveWorkout, refresh, elapsedSeconds }}>
      {children}
    </ActiveWorkoutContext.Provider>
  );
};
